import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Flex,
  Span,
  LinkButton,
  IconOnePass,
  Box,
  IconCatch,
  IconKmart,
  IconTarget,
  IconTick,
  Link,
} from "@catchoftheday/cg-components";
import { useEventBus, legacyEventBus } from "@catchoftheday/state";
import { withApollo } from "@catchoftheday/graphql-utils";
import {
  emitUpsellModalAction,
  emitUpsellModalImpression,
} from "@catchoftheday/analytics";
import { TRIGGER_ONEPASS_UPSELL_MODAL_EVENT } from ".";
import { ONEPASS_LEARN_MORE, LOGIN_URL } from "@common-vendors/consts";
import { useCustomerQuery } from "@common-vendors/generated/graphql";
import { triggerOnePassLinkingModal } from "@common-vendors/components/OnePassLinkingModal";
import { generateOnePassUpsellUrl } from "@common-vendors/utilities";

const OnePassBenefit = ({ children }) => (
  <Flex py="6px">
    <Box alignSelf="center">
      <IconTick width="18px" mr="9px" color="onePass.textPrimary" />
    </Box>
    <Span fontSize={{ xs: "sm", md: "base" }} fontWeight="medium">
      {children}
    </Span>
  </Flex>
);

export const OnePassUpsellModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPathname, setCurrentPathname] = useState("/");

  const { data } = useCustomerQuery({ ssr: false });
  const { membership, club } = data?.customer || {};
  const { subscription, linked } = membership || {};
  const { freeTrialEligible } = subscription || {};

  const isLoggedIn = !!data?.customer;
  const freeTrial =
    (!club?.active && !linked && !club?.freeTrialEligible) ||
    freeTrialEligible ||
    !membership ||
    !isLoggedIn;
  const hideSecondaryCTA = isLoggedIn && linked;

  const eventHandler = () => {
    emitUpsellModalImpression();
    setIsOpen(true);
  };

  useEventBus(TRIGGER_ONEPASS_UPSELL_MODAL_EVENT, () => {
    eventHandler();
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentPathname(window?.location.pathname);
    }
    legacyEventBus?.on(TRIGGER_ONEPASS_UPSELL_MODAL_EVENT.type, eventHandler);
    return () => {
      legacyEventBus?.detach(
        TRIGGER_ONEPASS_UPSELL_MODAL_EVENT.type,
        eventHandler
      );
    };
  }, []);

  const onMainCTAClick = useCallback(() => {
    emitUpsellModalAction({ label: freeTrial ? "freeTrial" : "join" });
  }, [freeTrial]);

  const onClose = useCallback(() => {
    emitUpsellModalAction({ label: "close" });
    setIsOpen(false);
  }, []);

  const onLoginClick = useCallback(() => {
    emitUpsellModalAction({ label: "login" });
  }, []);

  const onLinkClick = useCallback(() => {
    emitUpsellModalAction({ label: "link" });
    if (typeof window !== "undefined") {
      onClose();
      triggerOnePassLinkingModal({
        originalUrl: currentPathname,
      });
    }
  }, [currentPathname, onClose]);

  const onLearnMoreClick = useCallback(() => {
    emitUpsellModalAction({ label: "learnMore" });
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      zIndex={101}
      maxWidth={{ sm: "400px", md: "500px" }}
      height={{ sm: "auto" }}
      px={{ sm: "8px", md: "38px" }}
      borderRadius="16px"
      closeButtonProps={{
        bg: "onePass.primaryDarker",
        width: "36px",
        height: "36px",
      }}
      closeIconProps={{ width: "16px", height: "16px" }}
      onRequestClose={onClose}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        fontFamily="onePass"
        color="onePass.textPrimary"
      >
        <Flex>
          <Span fontSize={{ xs: "base", md: "2xl" }} fontWeight="bold">
            Join
          </Span>
          <IconOnePass
            width={{ xs: "96px", md: "128px" }}
            color="onePass.primary"
            aria-label="OnePass"
            mt={{ xs: "-4px", md: "5px" }}
            ml={{ xs: "5px", md: "0px" }}
          />
        </Flex>
        <Span
          fontSize={{ xs: "sm", md: "lg" }}
          fontWeight="medium"
          textAlign="center"
          mt={{ xs: "6px", md: "8px" }}
        >
          Enjoy a range of benefits from your favourite retail brands.
        </Span>
        <Flex
          width={{ sm: "70%", md: "85%" }}
          minWidth="220px"
          alignItems="center"
          justifyContent="space-between"
        >
          <IconCatch height="80px" width="80px" aria-label="Catch" />
          <IconKmart
            height="80px"
            width="80px"
            mx={{ xs: "20px", lg: "24px" }}
            aria-label="Kmart"
          />
          <IconTarget height="80px" width="80px" aria-label="Target" />
        </Flex>
        <Span
          fontSize={{ xs: "base", md: "l" }}
          fontWeight="bold"
          textTransform="capitalize"
        >
          OnePass Member Benefits:
        </Span>
        <Box mb="24px">
          <OnePassBenefit>
            Free delivery on eligible items at all OnePass retail brands
          </OnePassBenefit>
          <OnePassBenefit>
            Member-only pricing on thousands of products at Catch
          </OnePassBenefit>
          <OnePassBenefit>
            Access to weekly exclusive member deals at Catch
          </OnePassBenefit>
        </Box>

        {/* TODO: (DK) Use incoming price data once it becomes available. */}
        <Flex mb="12px" alignItems="end">
          <Span
            lineHeight="normal"
            fontSize={{ xs: "lg", md: "2xl" }}
            fontWeight="bold"
          >
            $4
          </Span>
          <Span fontSize={{ xs: "sm", md: "lg" }} fontWeight="bold">
            /month
          </Span>
          <Span mx="5px" fontSize={{ xs: "sm", md: "lg" }} fontWeight="normal">
            or
          </Span>
          <Span
            lineHeight="normal"
            fontSize={{ xs: "lg", md: "2xl" }}
            fontWeight="bold"
          >
            $40
          </Span>
          <Span fontSize={{ xs: "sm", md: "lg" }} fontWeight="bold">
            /year
          </Span>
        </Flex>

        {/* TODO: (DK) Use incoming trial days data once it becomes available. */}
        <Span
          fontSize={{ xs: "xs", md: "base" }}
          fontWeight="medium"
          textAlign="center"
          color="textTertiary"
        >
          Cancel anytime. {freeTrial && "Billing starts at end of trial."}
        </Span>

        <LinkButton
          mt="16px"
          mb={hideSecondaryCTA ? "0px" : "16px"}
          href={generateOnePassUpsellUrl({
            url: currentPathname,
            cta: freeTrial ? "start-free-trial" : "join",
          })}
          variant="onePassPrimary"
          sizeVariant="md"
          fontWeight="semibold"
          textTransform="uppercase"
          whiteSpace="wrap"
          onClick={onMainCTAClick}
        >
          {freeTrial ? "Start free 30 day trial" : "Join OnePass today"}
        </LinkButton>

        {hideSecondaryCTA ? null : (
          <Flex alignItems="end" fontWeight="bold" fontSize="xs">
            <Span
              mr="5px"
              fontWeight="medium"
              fontSize="xs"
              color="onePass.textPrimary"
            >
              Already a member?
            </Span>
            {!isLoggedIn ? (
              <Link
                color="onePass.primary"
                href={LOGIN_URL}
                textDecoration="underline"
                onClick={onLoginClick}
              >
                Log in
              </Link>
            ) : (
              <Link
                role="button"
                color="onePass.primary"
                textDecoration="underline"
                onClick={onLinkClick}
              >
                Link OnePass with Catch
              </Link>
            )}
          </Flex>
        )}
        <Link
          fontWeight="bold"
          fontSize="xs"
          mt="16px"
          color="onePass.primary"
          href={ONEPASS_LEARN_MORE}
          target="_blank"
          textTransform="capitalize"
          textDecoration="underline"
          onClick={onLearnMoreClick}
        >
          Learn more
        </Link>
      </Flex>
    </Modal>
  );
};

export const OnePassUpsellModalWithApollo = withApollo({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
})(OnePassUpsellModal);
